import { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import {
  Loader2, Mic, MicOff, Video as VideoIcon, VideoOff,
  Camera, AlertTriangle, ShieldAlert,
} from "lucide-react";

/**
 * Real LiveKit WebRTC stream renderer — DOM-isolated edition.
 *
 *  - The two DOM nodes that LiveKit injects <video>/<audio> elements into
 *    (`stageRef`, `previewRef`) are ALWAYS present and never re-rendered by
 *    React. They are siblings of the React-controlled status overlay.
 *  - We control visibility via `style.display` instead of conditionally
 *    unmounting siblings → React's reconciler never touches the DOM that
 *    LiveKit owns, which fixes the `insertBefore` crash.
 *  - DOM cleanup happens via the parent ref (`while (node.firstChild) remove`)
 *    inside try/catch, never via `track.detach()[i].remove()` which mutates
 *    DOM under React's feet.
 */
export default function LiveKitStream({ liveId, isOwner }) {
  const [phase, setPhase] = useState("init");
  const [error, setError] = useState("");
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [facing, setFacing] = useState("user");

  const stageRef = useRef(null);
  const previewRef = useRef(null);
  const roomRef = useRef(null);
  const lkRef = useRef(null);
  const cancelledRef = useRef(false);

  // Owner heartbeat: keeps the live "alive" server-side. If the artist closes
  // the app / loses connection without an explicit Stop Live, heartbeats stop
  // and the backend auto-ends the live once they go stale (see server.py
  // LIVE_HEARTBEAT_STALE_SECONDS), instead of leaving it live forever.
  useEffect(() => {
    if (!isOwner || phase !== "live") return;
    const beat = () => { api.post(`/lives/${liveId}/heartbeat`).catch(() => {}); };
    beat();
    const id = setInterval(beat, 20000);
    return () => clearInterval(id);
  }, [isOwner, phase, liveId]);

  // Best-effort fast path: try to end the live immediately when the owner
  // closes the tab/app, instead of waiting for the heartbeat to go stale.
  // fetch(..., { keepalive: true }) (unlike navigator.sendBeacon) lets us
  // still attach the Authorization header the /end endpoint requires.
  useEffect(() => {
    if (!isOwner) return;
    const endNow = () => {
      try {
        const token = localStorage.getItem("metazik_token");
        fetch(`${API_BASE}/lives/${liveId}/end`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          keepalive: true,
        });
      } catch { /* best-effort only — heartbeat expiry is the real backstop */ }
    };
    window.addEventListener("pagehide", endNow);
    return () => window.removeEventListener("pagehide", endNow);
  }, [isOwner, liveId]);

  // ---------- Safe DOM helpers ----------
  const safeClear = (node) => {
    if (!node) return;
    while (node.firstChild) {
      try { node.removeChild(node.firstChild); }
      catch { break; }
    }
  };

  const attachInto = (node, track, { mirror = false, muted = false } = {}) => {
    if (!node || !track) return;
    safeClear(node);
    try {
      const el = track.attach();
      el.style.cssText = `width:100%;height:100%;object-fit:cover;${mirror ? "transform:scaleX(-1);" : ""}`;
      el.setAttribute("playsinline", "true");
      el.setAttribute("autoplay", "true");
      if (muted) el.muted = true;
      node.appendChild(el);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("attach failed", e);
    }
  };

  // ---------- Permission ----------
  const requestPermissions = async () => {
    setPhase("permission");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      stream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("L'accès à la caméra/microphone a été refusé. Active les permissions dans les paramètres du navigateur puis recharge la page.");
        setPhase("denied");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setError("Aucune caméra ou microphone détecté sur cet appareil.");
        setPhase("error");
      } else if (name === "NotReadableError") {
        setError("La caméra est utilisée par une autre application. Ferme-la puis réessaie.");
        setPhase("error");
      } else {
        setError(e?.message || "Impossible d'accéder aux médias");
        setPhase("error");
      }
      return false;
    }
  };

  // ---------- Main effect ----------
  useEffect(() => {
    cancelledRef.current = false;
    // Capture refs at mount time so cleanup always targets the same DOM nodes
    // (avoids the `insertBefore` crash when React has already started unmounting them).
    const stageNode = stageRef.current;
    const previewNode = previewRef.current;
    let room;

    (async () => {
      try {
        const { data: cfg } = await api.get("/lives/webrtc-config");
        if (!cfg.enabled) { setPhase("disabled"); return; }
      } catch {
        setPhase("disabled"); return;
      }

      if (isOwner) {
        const ok = await requestPermissions();
        if (!ok || cancelledRef.current) return;
      }

      let token, url;
      try {
        const { data } = await api.post(`/lives/${liveId}/webrtc-token`);
        token = data.token; url = data.url;
      } catch (e) {
        setError(e.response?.data?.detail || "Token error");
        setPhase("error");
        return;
      }
      if (cancelledRef.current) return;
      setPhase("connecting");

      const lk = await import("livekit-client");
      lkRef.current = lk;

      room = new lk.Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { facingMode: "user", resolution: lk.VideoPresets.h720.resolution },
      });
      roomRef.current = room;

      // Remote tracks → stage container
      room.on(lk.RoomEvent.TrackSubscribed, (track) => {
        if (cancelledRef.current) return;
        // For each subscribed track, append a new media element to the stage.
        // We don't clear the stage because there can be multiple participants.
        if (!stageNode) return;
        try {
          const el = track.attach();
          el.style.cssText = "width:100%;height:100%;object-fit:cover;position:absolute;inset:0;";
          el.setAttribute("playsinline", "true");
          el.setAttribute("autoplay", "true");
          el.dataset.lkSid = track.sid || "";
          stageNode.appendChild(el);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("stage attach failed", e);
        }
      });
      room.on(lk.RoomEvent.TrackUnsubscribed, (track) => {
        if (cancelledRef.current) return;
        // Remove via the parent (never via element.remove() while React is rendering)
        if (!stageNode || !track?.sid) return;
        try {
          const sid = track.sid;
          // detach but immediately remove from our managed parent
          const els = track.detach();
          els.forEach((el) => {
            if (el && el.parentNode === stageNode) {
              try { stageNode.removeChild(el); } catch { /* ignore */ }
            }
          });
          // safety: also remove any orphan with the same sid
          stageNode.querySelectorAll(`[data-lk-sid="${sid}"]`).forEach((n) => {
            try { stageNode.removeChild(n); } catch { /* ignore */ }
          });
        } catch { /* ignore */ }
      });

      room.on(lk.RoomEvent.LocalTrackPublished, (publication) => {
        if (cancelledRef.current) return;
        if (publication.kind === "video" && publication.track) {
          attachInto(previewNode, publication.track, { mirror: facing === "user", muted: true });
        }
      });

      room.on(lk.RoomEvent.Disconnected, () => {
        if (!cancelledRef.current) setPhase("ended");
      });

      try {
        await room.connect(url, token);
        if (cancelledRef.current) return;

        if (isOwner) {
          await room.localParticipant.setMicrophoneEnabled(true);
          await room.localParticipant.setCameraEnabled(true);
          const pub = room.localParticipant.getTrackPublication(lk.Track.Source.Camera);
          if (pub?.track) attachInto(previewNode, pub.track, { mirror: facing === "user", muted: true });
        } else {
          for (const participant of room.remoteParticipants.values()) {
            participant.trackPublications.forEach((p) => {
              if (p.track && stageNode) {
                try {
                  const el = p.track.attach();
                  el.style.cssText = "width:100%;height:100%;object-fit:cover;position:absolute;inset:0;";
                  el.setAttribute("playsinline", "true");
                  el.setAttribute("autoplay", "true");
                  el.dataset.lkSid = p.track.sid || "";
                  stageNode.appendChild(el);
                } catch { /* ignore */ }
              }
            });
          }
        }

        if (!cancelledRef.current) setPhase("live");
      } catch (e) {
        setError(e?.message || "Connexion impossible");
        setPhase("error");
      }
    })();

    return () => {
      // Mark as cancelled FIRST so any in-flight async work / event handlers bail out
      // before they touch the DOM that React is about to unmount.
      cancelledRef.current = true;
      // 1. Disconnect the room (any RoomEvent.* handlers that fire are now no-ops).
      const r = roomRef.current;
      if (r) {
        try { r.removeAllListeners?.(); } catch { /* ignore */ }
        try { r.disconnect(); } catch { /* ignore */ }
      }
      roomRef.current = null;
      // 2. Clean up DOM children inside the captured nodes — done THROUGH the parent
      //    so React stays consistent. Using the captured `stageNode`/`previewNode`
      //    avoids race conditions where stageRef.current may already be null.
      safeClear(stageNode);
      safeClear(previewNode);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveId, isOwner]);

  // ---------- Controls ----------
  const toggleMic = async () => {
    if (!roomRef.current || !isOwner) return;
    const next = !micOn;
    await roomRef.current.localParticipant.setMicrophoneEnabled(next);
    setMicOn(next);
  };
  const toggleCam = async () => {
    if (!roomRef.current || !isOwner) return;
    const next = !camOn;
    await roomRef.current.localParticipant.setCameraEnabled(next);
    setCamOn(next);
  };
  const switchCamera = async () => {
    if (!roomRef.current || !isOwner || !lkRef.current) return;
    try {
      const room = roomRef.current;
      const lk = lkRef.current;

      // Passing facingMode through setCameraEnabled()'s capture options
      // still wasn't reliably swapping the physical camera on real devices
      // — some browsers keep reusing the already-live MediaStreamTrack on a
      // disable/enable cycle instead of re-requesting getUserMedia with the
      // new constraint. room.switchActiveDevice() with an explicit deviceId
      // is LiveKit's dedicated hot-swap API and forces a genuinely different
      // camera device.
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === "videoinput");
      if (cams.length < 2) return; // only one physical camera — nothing to switch to

      const pub = room.localParticipant.getTrackPublication(lk.Track.Source.Camera);
      const currentDeviceId = pub?.track?.mediaStreamTrack?.getSettings?.().deviceId;
      const nextCam = cams.find((d) => d.deviceId && d.deviceId !== currentDeviceId) || cams[1] || cams[0];

      await room.switchActiveDevice("videoinput", nextCam.deviceId);

      const label = (nextCam.label || "").toLowerCase();
      const guessed = /back|rear|environment/.test(label) ? "environment" : /front|user|face/.test(label) ? "user" : null;
      const next = guessed || (facing === "user" ? "environment" : "user");

      const freshPub = room.localParticipant.getTrackPublication(lk.Track.Source.Camera);
      if (freshPub?.track) attachInto(previewRef.current, freshPub.track, { mirror: next === "user", muted: true });
      setFacing(next);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("camera switch failed", e);
    }
  };

  // ---------- Render ----------
  // The two DOM containers below are ALWAYS rendered — never conditional —
  // so React never has to insert/remove siblings around DOM that LiveKit owns.
  const showStage = phase === "live" || phase === "connecting";
  const showPreview = isOwner && (phase === "live" || phase === "connecting");

  return (
    <div className="absolute inset-0 bg-black overflow-hidden">
      {/* DOM-only — never touched by React after mount */}
      <div
        ref={stageRef}
        className="absolute inset-0"
        style={{ display: showStage && !isOwner ? "block" : (showStage && isOwner ? "none" : "none") }}
        data-testid="livekit-stage"
      />
      <div
        ref={previewRef}
        className="absolute inset-0 overflow-hidden"
        style={{ display: showPreview ? "block" : "none" }}
        data-testid="livekit-preview"
      />

      {/* React-controlled status overlays — siblings, never inside the refs */}
      <StatusOverlay phase={phase} error={error} />

      {isOwner && phase === "live" && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2" data-testid="lk-controls">
          <button onClick={toggleMic} className={`w-11 h-11 rounded-full grid place-items-center ${micOn ? "bg-white/10" : "bg-red-500"} border border-white/20 backdrop-blur`} data-testid="lk-mic" title={micOn ? "Couper le micro" : "Réactiver le micro"}>
            {micOn ? <Mic className="w-5 h-5 text-white" /> : <MicOff className="w-5 h-5 text-white" />}
          </button>
          <button onClick={toggleCam} className={`w-11 h-11 rounded-full grid place-items-center ${camOn ? "bg-white/10" : "bg-red-500"} border border-white/20 backdrop-blur`} data-testid="lk-cam" title={camOn ? "Couper la caméra" : "Réactiver la caméra"}>
            {camOn ? <VideoIcon className="w-5 h-5 text-white" /> : <VideoOff className="w-5 h-5 text-white" />}
          </button>
          <button onClick={switchCamera} className="w-11 h-11 rounded-full grid place-items-center bg-white/10 border border-white/20 backdrop-blur" data-testid="lk-switch" title="Changer de caméra">
            <Camera className="w-5 h-5 text-white" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Status overlay component (kept in a separate React subtree) ---------- */

function StatusOverlay({ phase, error }) {
  if (phase === "live") return null;

  if (phase === "disabled") {
    return <Banner icon={<Loader2 className="w-12 h-12 text-[#FF0050] animate-spin mx-auto" />} title="Lives en mode placeholder" sub="WebRTC keys non configurées" testid="lk-disabled" />;
  }
  if (phase === "permission") {
    return <Banner icon={<Camera className="w-12 h-12 text-[#FFD60A] mx-auto" />} title="Autoriser l'accès à la caméra" sub="Clique sur « Autoriser » dans la fenêtre du navigateur pour démarrer le live." testid="lk-permission" />;
  }
  if (phase === "denied") {
    return (
      <Banner
        icon={<ShieldAlert className="w-12 h-12 text-red-400 mx-auto" />}
        title="Accès refusé"
        sub={error}
        testid="lk-denied"
        action={(
          <button onClick={() => window.location.reload()} className="mt-3 px-4 py-2 rounded-full text-xs font-bold bg-white text-black hover:opacity-90" data-testid="lk-reload">
            Recharger après autorisation
          </button>
        )}
      />
    );
  }
  if (phase === "error") {
    return <Banner icon={<AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />} title="Erreur live" sub={error} testid="lk-error" />;
  }
  if (phase === "ended") {
    return <Banner icon={<VideoOff className="w-12 h-12 text-zinc-400 mx-auto" />} title="Ce live est terminé" sub="Reviens plus tard pour le prochain live." testid="lk-ended" />;
  }
  // phase === "init" or "connecting"
  return (
    <div className="absolute inset-0 z-10 grid place-items-center bg-black/50 pointer-events-none">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#FF0050] animate-spin mx-auto" />
        <p className="mt-3 text-white text-sm font-bold">Connexion à LiveKit…</p>
      </div>
    </div>
  );
}

function Banner({ icon, title, sub, action, testid }) {
  return (
    <div className="absolute inset-0 z-20 grid place-items-center bg-black" data-testid={testid}>
      <div className="text-center px-6 max-w-sm">
        {icon}
        <p className="text-white mt-4 font-black font-display text-lg">{title}</p>
        {sub && <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{sub}</p>}
        {action}
      </div>
    </div>
  );
}
